import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
    BarChart3,
    Building2,
    Check,
    ExternalLink,
    MessageCircle,
    Newspaper,
    Sparkles,
    Video,
    X,
    type LucideIcon,
} from "lucide-react";

import { useAcknowledgeAnnouncements } from "../application/useAnnouncements";
import {
    type Announcement,
    type AnnouncementIcon,
    announcementCopyKeys,
    announcementLinks,
} from "../domain/announcement";

const ICONS: Record<AnnouncementIcon, LucideIcon> = {
    building: Building2,
    sparkles: Sparkles,
    message: MessageCircle,
    chart: BarChart3,
    video: Video,
};

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

interface Props {
    pending: Announcement[];
}

/**
 * "What's new in Alchely": every announcement the therapist has not seen, once.
 *
 * The list is frozen when the modal opens. Dismissing acknowledges it, which
 * empties `pending` at once — without the snapshot the content would vanish
 * while the exit animation is still playing. Every way out counts as seen,
 * including pages the therapist did not turn to.
 */
export default function AnnouncementsModal({ pending }: Props) {
    const { mutate: acknowledge } = useAcknowledgeAnnouncements();
    const [shown, setShown] = useState<Announcement[] | null>(null);
    const [dismissed, setDismissed] = useState(false);

    if (shown === null && pending.length > 0) {
        setShown(pending);
    }

    const dismiss = useCallback(() => {
        if (shown === null || dismissed) return;
        setDismissed(true);
        acknowledge(shown.map((announcement) => announcement.id));
    }, [shown, dismissed, acknowledge]);

    return (
        <AnimatePresence>
            {shown !== null && !dismissed && (
                <AnnouncementsDialog key="announcements" announcements={shown} onDismiss={dismiss} />
            )}
        </AnimatePresence>
    );
}

interface DialogProps {
    announcements: Announcement[];
    onDismiss: () => void;
}

function AnnouncementsDialog({ announcements, onDismiss }: DialogProps) {
    const { t, i18n } = useTranslation();
    const [index, setIndex] = useState(0);
    const dialogRef = useRef<HTMLDivElement>(null);
    const primaryRef = useRef<HTMLButtonElement>(null);

    const total = announcements.length;
    const isLast = index === total - 1;
    const announcement = announcements[index];
    const copy = announcementCopyKeys(announcement);
    const links = announcementLinks(announcement, i18n.language);
    const Icon = ICONS[announcement.icon];

    useEffect(() => {
        primaryRef.current?.focus();
    }, []);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onDismiss();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            // Keep Tab inside the dialog: behind it is a page the backdrop
            // says is not there.
            const focusable = Array.from(
                dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onDismiss]);

    return (
        <>
            <motion.div
                key="announcements-backdrop"
                data-testid="announcements-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
                onClick={onDismiss}
            />
            <motion.div
                key="announcements-dialog"
                initial={{ opacity: 0, scale: 0.95, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 16 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="pointer-events-none fixed inset-0 z-[9999] flex items-end justify-center p-4 sm:items-center"
            >
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="announcement-title"
                    aria-describedby="announcement-body"
                    // Capped to the viewport with only the copy scrolling, so
                    // the buttons never leave the screen.
                    className="pointer-events-auto relative flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg flex-col rounded-3xl border border-gray-200/80 bg-surface-raised p-6 shadow-2xl dark:border-white/10 sm:p-8"
                >
                    <button
                        type="button"
                        onClick={onDismiss}
                        aria-label={t("announcements.close")}
                        className="absolute right-4 top-4 text-content-tertiary transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <p className="flex items-center gap-1.5 pr-8 text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                        <Newspaper className="h-3.5 w-3.5" aria-hidden="true" />
                        {t("announcements.eyebrow")}
                    </p>

                    <div className="custom-scrollbar mt-5 min-h-0 overflow-y-auto">
                        {announcement.image && copy.imageAlt ? (
                            <img
                                src={announcement.image.src}
                                alt={t(copy.imageAlt)}
                                className="aspect-[12/5] w-full rounded-2xl border border-gray-200/80 object-cover dark:border-white/10"
                            />
                        ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500">
                                <Icon className="h-6 w-6" aria-hidden="true" />
                            </div>
                        )}
                        <h3
                            id="announcement-title"
                            className="mt-4 text-xl font-bold text-content-primary"
                        >
                            {t(copy.title)}
                        </h3>
                        <p
                            id="announcement-body"
                            className="mt-2 text-sm leading-relaxed text-content-tertiary"
                        >
                            {t(copy.body)}
                        </p>
                        {copy.highlights.length > 0 && (
                            <ul className="mt-5 space-y-3">
                                {copy.highlights.map((key) => (
                                    <li
                                        key={key}
                                        className="flex items-start gap-3 text-sm leading-snug text-content-primary"
                                    >
                                        <Check
                                            className="mt-0.5 h-4 w-4 shrink-0 text-brand-500"
                                            aria-hidden="true"
                                        />
                                        <span>{t(key)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {(links.moreInfo || links.documentation) && (
                            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                                {links.moreInfo && (
                                    <SiteLink href={links.moreInfo} label={t("announcements.moreInfo")} />
                                )}
                                {links.documentation && (
                                    <SiteLink
                                        href={links.documentation}
                                        label={t("announcements.documentation")}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex shrink-0 flex-wrap items-center gap-3">
                        {total > 1 && (
                            <span className="text-xs text-content-tertiary" aria-live="polite">
                                {t("announcements.pager", { current: index + 1, total })}
                            </span>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                            {index > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setIndex(index - 1)}
                                    className="rounded-xl px-4 py-2.5 text-sm font-medium text-content-tertiary transition-colors hover:bg-gray-100 hover:text-content-primary dark:hover:bg-white/5"
                                >
                                    {t("announcements.previous")}
                                </button>
                            )}
                            <button
                                ref={primaryRef}
                                type="button"
                                onClick={isLast ? onDismiss : () => setIndex(index + 1)}
                                className="inline-flex items-center justify-center rounded-xl bg-brand-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition-colors hover:bg-brand-600"
                            >
                                {isLast ? t("announcements.dismiss") : t("announcements.next")}
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </>
    );
}

/**
 * A page on the public site, in its own tab: reading it is not dismissing
 * the news, so the modal stays where it was.
 */
function SiteLink({ href, label }: { href: string; label: string }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
            {label}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
    );
}
